import React, { useEffect, useState } from "react";
import { ApiCall } from "components/common/services/ApiServices";
import { Link, useNavigate } from "react-router-dom";
import Button from "components/common/atoms/Button";
import Pagination from "components/common/atoms/Pagination";
import CustomNotify from "components/common/atoms/CustomNotify";
import EditIcon from "static/images/EditIcon";
import * as ENDPOINTS from "routes/ApiEndpoints";
import DeleteIcon from "static/images/DeleteIcon";
import {
  t,
  translate,
} from "components/CentralDataMangement/translation/Translation";
import ModalPopup from "components/common/atoms/ModalPopup";
import Reset from "static/images/Reset";
import { faMagnifyingGlass, faPlus } from "@fortawesome/free-solid-svg-icons";
import LinkTo from "components/common/atoms/LinkTo";
import Title from "components/common/atoms/Title";
import { Spinner } from "react-bootstrap";
import { LabelWithInputField } from "components/common/molecules/LabelWithInputField";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";
import ResetBtn from "components/common/atoms/ResetBtn";
import SpinnerWrapper from "components/common/atoms/SpinnerWrapper";
import NoRecords from "components/common/atoms/NoRecords";

interface manageData {
  employeetype_name: string;
  id: number;
}

interface empProps {
  employeeType: "";
}

const ManageEmployeeType: React.FC = () => {
  //initiliaze the formData state for search fields
  const [formData, setFormData] = useState<empProps>({
    employeeType: "",
  });

  const navigate = useNavigate();

  //state for to store the functions linked countries
  const [empTypes, setEmpTypes] = useState([] as manageData[]);

  //state for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteID, setDeleteId] = useState<number | undefined>();

  useEffect(() => {
    fetchEmpTypesBySearch();
  }, []);

  //API call for to fetch Employee types
  const fetchEmpTypesBySearch = async (formData = {}, page = 1) => {
    const requestData = {
      ...formData,
      page: page,
      type: "employeeType",
    };

    const response = await ApiCall.service(
      ENDPOINTS.CONF_SETTINGS_GET,
      "POST",
      requestData,
      false,
      "central-data-management"
    );

    if (response?.status === 200) {
      setEmpTypes(response?.data?.data);
      setTotalPages(response?.data?.totalPages);
      setLoading(false);
    }else {
      CustomNotify({ type: "error", message: t("Some error occured") });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.employeeType.length === 0) {
      CustomNotify({
        type: "warning",
        message: t("Search input not provided") + ".",
      });
      fetchEmpTypesBySearch();
    } else {
      fetchEmpTypesBySearch(formData, 1);
    }
  };

  const handleReset = () => {
    // Clear the selected values in the dropdowns
    setFormData({
      employeeType: "",
    });
    setCurrentPage(1);
    fetchEmpTypesBySearch();
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchEmpTypesBySearch(formData, newPage);
  };

  const deleteEmpType = (id: number | undefined) => {
    setShowModal(true);
    setDeleteId(id);
  };

  const handleModalClose = () => setShowModal(!showModal);

  const handleSaveChanges = async () => {
    setShowModal(false);
    if (deleteID) {
      const requestData = {
        id: deleteID,
        type: "employeeType",
      };
      const url = ENDPOINTS.CONF_SETTINGS_DELETE;
      const response = await ApiCall.service(
        url,
        "POST",
        requestData,
        false,
        "central-data-management"
      );
      if (response?.status === 200) {
        fetchEmpTypesBySearch();
        CustomNotify({ type: "success", message: t(response?.msg) });
      }else {
      CustomNotify({ type: "error", message: t("Some error occured") });
    }
    }
  };

  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };
  return (
    <AccessControl
      requiredPermissions={[
        {
          permission: "Employee type",
          read: true,
        },
      ]}
      renderNoAccess={true}
    >
      <Title title={t("Employee types")} />
      <div className="row search-bar">
        <div className="col-8">
          <LabelWithInputField
            handleChange={handleFieldChange}
            name="employeeType"
            value={formData.employeeType}
            label={t("Employee type")}
          />
        </div>
        <div className="col-4 mt-34">
          <div className="d-flex align-items-center">
            <ResetBtn handleResetClick={handleReset} />
            <Button
              title={t("Search")}
              type="submit"
              icon={faMagnifyingGlass}
              handleClick={handleSubmit}
              className="form-button shadow-none px-3 search-btns text-start position-relative"
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="position-relative tableMainWrapper">
            <AccessControl
              requiredPermissions={[
                {
                  permission: "Employee type",
                  create: true,
                },
              ]}
            >
              <div className="row">
                <div className="ManageCreateBtn">
                  <LinkTo
                    pagelink="/add/employee-type"
                    title={t("Add employee type")}
                    icon={faPlus}
                  />
                </div>
              </div>
            </AccessControl>
            <div className="table-responsive tableSection paddingTop2">
              <table className="table table-hover">
                <thead>
                  <tr className="TableHeader">
                    <th className="ps-lg-4" style={{ width: "60%" }}>
                      {t("Employee type")}
                    </th>
                    <AccessControl
                      requiredPermissions={[
                        {
                          permission: "Employee type",
                          update: true,
                          delete: true,
                        },
                      ]}
                      actions={true}
                      strict={false}
                    >
                      <th>{t("Actions")}</th>
                    </AccessControl>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <SpinnerWrapper headerLength={3} />
                  ) : (
                    <>
                      {empTypes && empTypes.length > 0 ? (
                        empTypes.map((empType) => (
                          <tr
                            key={empType.id}
                            className="border-bottom mb-3 box-shadow align-middle"
                          >
                            {/* <td className="ps-lg-4">{pageIndex++}</td> */}
                            <td
                              className="ps-lg-4"
                              data-label={t("Employee type")}
                            >
                              {t(`${empType.employeetype_name}`)}
                            </td>
                            <AccessControl
                              requiredPermissions={[
                                {
                                  permission: "Employee type",
                                  update: true,
                                  delete: true,
                                },
                              ]}
                              actions={true}
                              strict={false}
                            >
                              <td className="table-action-icons px-2">
                                <div className="d-none d-md-none d-lg-block">
                                  <AccessControl
                                    requiredPermissions={[
                                      {
                                        permission: "Employee type",
                                        update: true,
                                      },
                                    ]}
                                  >
                                    <Link
                                      to={`/edit/employee-type/${empType.id}`}
                                      className="btn p-0 border-0 me-2"
                                      title={t("Edit")}
                                    >
                                      <EditIcon />
                                    </Link>
                                  </AccessControl>
                                  <AccessControl
                                    requiredPermissions={[
                                      {
                                        permission: "Employee type",
                                        delete: true,
                                      },
                                    ]}
                                  >
                                    {empType.id > 13 && (
                                      <Button
                                        className="btn p-0 border-0"
                                        handleClick={() =>
                                          deleteEmpType(empType.id)
                                        }
                                      >
                                        <span title={t("Delete")}>
                                          <DeleteIcon />
                                        </span>
                                      </Button>
                                    )}
                                  </AccessControl>
                                </div>

                                {/* PWA */}
                                <div className="d-block d-md-block d-lg-none">
                                  <AccessControl
                                    requiredPermissions={[
                                      {
                                        permission: "Employee type",
                                        update: true,
                                      },
                                    ]}
                                  >
                                    <Link
                                      to={`/edit/employee-type/${empType.id}`}
                                      className="btn p-0 border-0 w-100"
                                      title={t("Edit")}
                                    >
                                      <Button
                                        title={t("Edit")}
                                        className=" mb-2 button-width edit-btn rounded-3"
                                      />
                                    </Link>
                                  </AccessControl>
                                  <AccessControl
                                    requiredPermissions={[
                                      {
                                        permission: "Employee type",
                                        delete: true,
                                      },
                                    ]}
                                  >
                                    {empType.id > 13 && (
                                      <Button
                                        className="btn p-0 border-0 w-100"
                                        handleClick={() =>
                                          deleteEmpType(empType.id)
                                        }
                                        title={t("Delete")}
                                      />
                                    )}
                                  </AccessControl>
                                </div>
                              </td>
                            </AccessControl>
                          </tr>
                        ))
                      ) : (
                        <NoRecords headerLength={7} />
                      )}
                    </>
                  )}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="pagination justify-content-center align-items-center">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(newPage) => handlePageChange(newPage)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="row backPadding">
        <div className="col-md-6 align-self-center">
          <Link
            to=""
            className="back-btn text-decoration-underline"
            onClick={() => navigate(-1)}
          >
            {t("Back")}
          </Link>
        </div>
      </div>
      <ModalPopup
        show={showModal}
        onHide={handleModalClose}
        title={t("Delete confirmation")}
        body={t("Are you sure want to delete") + "?"}
        onCloseButtonClick={handleModalClose}
        onConfirmButtonClick={handleSaveChanges}
        closeTitle={t("No")}
        confirmTitle={t("Yes")}
      />
    </AccessControl>
  );
};

export default translate(ManageEmployeeType);
